import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { subscriptionApi, type PlanFeatures } from '../api/subscription';

interface FeatureGateProps {
  feature: keyof PlanFeatures;
  children: React.ReactNode;
  upgradeMessage?: string;
  requiredPlan?: string;
}

export function FeatureGate({ feature, children, upgradeMessage, requiredPlan = 'Prima' }: FeatureGateProps) {
  const navigate = useNavigate();
  const { data: sub } = useQuery({
    queryKey: ['subscription'],
    queryFn: subscriptionApi.get,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  // While loading, render children normally (optimistic)
  if (!sub) return <>{children}</>;

  const hasFeature = !sub.isExpired && Boolean(sub.features[feature]);
  if (hasFeature) return <>{children}</>;

  return (
    <div style={{
      position: 'relative',
      display: 'inline-block',
      width: '100%',
    }}>
      <div style={{ opacity: 0.35, pointerEvents: 'none', userSelect: 'none' }}>
        {children}
      </div>
      <div style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--overlay-bg)',
        borderRadius: 8,
        border: '1.5px dashed var(--border-input)',
        gap: '0.5rem',
        cursor: 'pointer',
        padding: '1rem',
      }}
        onClick={() => navigate('/subscription')}
      >
        <span style={{ fontSize: '1.5rem' }}>🔒</span>
        <div style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--text-2)', textAlign: 'center' }}>
          {upgradeMessage ?? `Available on ${requiredPlan} & above`}
        </div>
        <button
          onClick={e => { e.stopPropagation(); navigate('/subscription'); }}
          style={{ background: 'var(--primary)', color: 'white', border: 'none', borderRadius: 6, padding: '0.3rem 0.9rem', fontWeight: 600, cursor: 'pointer', fontSize: '0.78rem' }}>
          Upgrade Plan
        </button>
      </div>
    </div>
  );
}

// Inline lock badge — use for table cells or small UI elements
export function FeatureLockBadge({ label, requiredPlan = 'Prima' }: { label: string; requiredPlan?: string }) {
  const navigate = useNavigate();
  return (
    <span
      onClick={() => navigate('/subscription')}
      title={`${label} — Available on ${requiredPlan} plan`}
      style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', background: 'var(--surface-alt)', border: '1px solid var(--border)', borderRadius: 4, padding: '0.15rem 0.5rem', fontSize: '0.72rem', color: 'var(--text-muted)', cursor: 'pointer', whiteSpace: 'nowrap' }}>
      🔒 {requiredPlan}+
    </span>
  );
}
